// Resolves which Firebase project the backoffice talks to. Pure (no SDK) so
// the production guards are unit-tested.
//
// - Development, tests and e2e: the Emulator Suite with the demo project.
// - Production build: the real project from VITE_FIREBASE_CONFIG (the JSON
//   printed by `firebase apps:sdkconfig WEB <appId>`) plus the reCAPTCHA
//   Enterprise site key for App Check. A production build without them must
//   never fall back to the emulators: it would ship a site that sends admin
//   passwords to 127.0.0.1. It fails at startup instead, saying what is
//   missing (same rule as the Flutter app, lib/nucleo/firebase/entorno.dart).

export interface EnvFirebase {
  readonly PROD: boolean;
  readonly VITE_FIREBASE_CONFIG?: string;
  readonly VITE_RECAPTCHA_SITE_KEY?: string;
  readonly VITE_USAR_EMULADORES?: string;
}

export interface OpcionesFirebase {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  appId: string;
  messagingSenderId?: string;
  measurementId?: string;
}

export interface ConfigFirebase {
  opciones: OpcionesFirebase;
  usaEmuladores: boolean;
  /** Only set outside the emulators; App Check is skipped when empty. */
  recaptchaSiteKey?: string;
}

const PROYECTO_DEMO = 'demo-taller-maestro';

/** Demo config: the emulators accept any API key for demo-* projects. */
const opcionesDemo: OpcionesFirebase = {
  apiKey: 'demo-api-key',
  authDomain: `${PROYECTO_DEMO}.firebaseapp.com`,
  projectId: PROYECTO_DEMO,
  storageBucket: `${PROYECTO_DEMO}.appspot.com`,
  appId: '1:000000000000:web:backoffice',
};

const COMO_GENERARLA =
  'Genera apps/backoffice/.env.production.local con `firebase apps:sdkconfig` ' +
  '(docs/setup-firebase.md §6).';

function leerConfig(json: string): OpcionesFirebase {
  let c: Record<string, unknown>;
  try {
    c = JSON.parse(json) as Record<string, unknown>;
  } catch {
    throw new Error(`VITE_FIREBASE_CONFIG no es un JSON válido. ${COMO_GENERARLA}`);
  }
  const texto = (k: keyof OpcionesFirebase) =>
    typeof c[k] === 'string' && c[k] !== '' ? c[k] : undefined;
  const opciones = {
    apiKey: texto('apiKey'),
    authDomain: texto('authDomain'),
    projectId: texto('projectId'),
    storageBucket: texto('storageBucket'),
    appId: texto('appId'),
  };
  const faltan = Object.entries(opciones)
    .filter(([, v]) => v === undefined)
    .map(([k]) => k);
  if (faltan.length > 0) {
    throw new Error(`A VITE_FIREBASE_CONFIG le falta ${faltan.join(', ')}. ${COMO_GENERARLA}`);
  }
  const sender = texto('messagingSenderId');
  const medicion = texto('measurementId');
  return {
    ...(opciones as Omit<OpcionesFirebase, 'messagingSenderId' | 'measurementId'>),
    ...(sender ? { messagingSenderId: sender } : {}),
    ...(medicion ? { measurementId: medicion } : {}),
  };
}

export function resolverConfig(env: EnvFirebase): ConfigFirebase {
  const pideEmuladores = env.VITE_USAR_EMULADORES ? env.VITE_USAR_EMULADORES === 'true' : !env.PROD;

  if (pideEmuladores) {
    if (env.PROD && env.VITE_FIREBASE_CONFIG) {
      throw new Error(
        'Build de producción con la config real y VITE_USAR_EMULADORES=true: elige uno.',
      );
    }
    return { opciones: opcionesDemo, usaEmuladores: true };
  }

  if (!env.VITE_FIREBASE_CONFIG) {
    throw new Error(`Falta VITE_FIREBASE_CONFIG (proyecto Firebase real). ${COMO_GENERARLA}`);
  }
  const opciones = leerConfig(env.VITE_FIREBASE_CONFIG);
  if (opciones.projectId.startsWith('demo-')) {
    throw new Error(`El proyecto ${opciones.projectId} solo existe en los emuladores.`);
  }
  const recaptchaSiteKey = env.VITE_RECAPTCHA_SITE_KEY?.trim();
  if (!recaptchaSiteKey) {
    throw new Error(
      `Falta VITE_RECAPTCHA_SITE_KEY (App Check con reCAPTCHA Enterprise). ${COMO_GENERARLA}`,
    );
  }
  return { opciones, usaEmuladores: false, recaptchaSiteKey };
}
